import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calculator, CheckCircle2, AlertCircle, RotateCcw, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANTONS = [
  { code: "GE", name: "Genève" },
  { code: "VD", name: "Vaud" },
  { code: "VS", name: "Valais" },
  { code: "FR", name: "Fribourg" },
  { code: "NE", name: "Neuchâtel" },
  { code: "JU", name: "Jura" },
  { code: "BE", name: "Berne" },
  { code: "ZH", name: "Zurich" },
  { code: "BS", name: "Bâle-Ville" },
  { code: "BL", name: "Bâle-Campagne" },
  { code: "AG", name: "Argovie" },
  { code: "SO", name: "Soleure" },
  { code: "LU", name: "Lucerne" },
  { code: "ZG", name: "Zoug" },
  { code: "SZ", name: "Schwytz" },
  { code: "UR", name: "Uri" },
  { code: "OW", name: "Obwald" },
  { code: "NW", name: "Nidwald" },
  { code: "GL", name: "Glaris" },
  { code: "SH", name: "Schaffhouse" },
  { code: "AR", name: "Appenzell Rh.-Ext." },
  { code: "AI", name: "Appenzell Rh.-Int." },
  { code: "SG", name: "Saint-Gall" },
  { code: "GR", name: "Grisons" },
  { code: "TG", name: "Thurgovie" },
  { code: "TI", name: "Tessin" },
];

const formatCHF = (n) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 })
    .format(Math.round(n || 0));

const initial = {
  situation: "celibataire",
  adultes: 1,
  enfants: 0,
  revenu_mensuel_brut: "",
  fortune: "",
  prime_mensuelle: "",
  canton: "GE",
};

const initialLead = {
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  consent: false,
};

export default function Simulator() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Lead capture (étape finale)
  const [lead, setLead] = useState(initialLead);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  const totalSteps = 3;
  const progress = ((step + (result ? 1 : 0)) / (totalSteps + 1)) * 100;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setL = (k, v) => setLead((d) => ({ ...d, [k]: v }));

  const handleSituation = (val) => {
    let adultes = 1;
    if (val === "couple") adultes = 2;
    if (val === "famille") adultes = 2;
    if (val === "famille_mono") adultes = 1;
    set("situation", val);
    set("adultes", adultes);
  };

  const validateStep = () => {
    if (step === 0) return !!data.situation;
    if (step === 1) {
      const r = parseFloat(data.revenu_mensuel_brut);
      const f = parseFloat(data.fortune);
      return !isNaN(r) && r >= 0 && !isNaN(f) && f >= 0;
    }
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      // Convert monthly gross to annual (approx -13% for social charges) for the calculator
      const monthlyBrut = Number(data.revenu_mensuel_brut);
      const annualNet = Math.round(monthlyBrut * 12 * 0.87);
      const payload = {
        situation: data.situation,
        adultes: Number(data.adultes),
        enfants: Number(data.enfants),
        revenu_annuel: annualNet,
        fortune: Number(data.fortune),
        prime_mensuelle: data.prime_mensuelle ? Number(data.prime_mensuelle) : null,
        canton: data.canton,
      };
      const { data: res } = await axios.post(`${API}/simulate`, payload);
      setResult(res);
      setStep(totalSteps);
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "simulator_complete", {
          eligible: res.eligible,
          subside_total_annuel: res.subside_total_annuel,
          canton: data.canton,
        });
      }
    } catch (e) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setData(initial);
    setResult(null);
    setStep(0);
    setLead(initialLead);
    setLeadSent(false);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent) {
      toast.error("Merci de cocher le consentement RGPD pour recevoir votre estimation.");
      return;
    }
    setLeadLoading(true);

    // Construit un message riche avec les inputs + résultats du simulateur
    const cantonName = CANTONS.find((c) => c.code === data.canton)?.name || data.canton;
    const lines = [
      "=== SIMULATION DE SUBSIDES ===",
      `Canton : ${cantonName} (${data.canton})`,
      `Méthode appliquée : ${result?.methode || "n/a"}`,
      `Situation : ${data.situation} — ${data.adultes} adulte(s), ${data.enfants} enfant(s)`,
      `Revenu mensuel BRUT déclaré : ${data.revenu_mensuel_brut || "n/a"} CHF`,
      `Fortune déclarée : ${data.fortune || "n/a"} CHF`,
      `Prime LAMal actuelle : ${data.prime_mensuelle || "non renseignée"} CHF/mois`,
      "",
      "=== RÉSULTAT ===",
      `Éligibilité : ${result?.eligible ? "OUI" : "NON"}`,
      `Subside total mensuel estimé : ${result?.subside_total_mensuel || 0} CHF`,
      `Subside total annuel estimé : ${result?.subside_total_annuel || 0} CHF`,
      `Reste à charge mensuel : ${result?.reste_a_charge_mensuel ?? "n/a"} CHF`,
      result?.is_approximation ? "[Approximation — barème GE appliqué]" : "",
      "",
      `Message du simulateur : ${result?.message || ""}`,
    ].filter(Boolean);

    try {
      await axios.post(`${API}/contact`, {
        prenom: lead.prenom,
        nom: lead.nom,
        email: lead.email,
        telephone: lead.telephone,
        consent: lead.consent,
        message: lines.join("\n"),
      });
      setLeadSent(true);
      toast.success("Estimation envoyée. Vous recevez un email de confirmation.");
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          method: "simulator_lead_capture",
          canton: data.canton,
          eligible: result?.eligible,
          subside_total_annuel: result?.subside_total_annuel,
        });
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur. Merci de réessayer.");
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <section
      id="simulateur"
      data-testid="simulator-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-spi-red mb-4">
            <Calculator className="w-3.5 h-3.5" strokeWidth={1.75} />
            Simulateur gratuit
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-spi-blue font-bold">
            Vérifiez vos subsides à l'assurance maladie en moins d'1 minute.
          </h2>
          <p className="mt-4 text-slate-600">
            Notre service vous accompagne pas à pas dans votre démarche de demande
            de subside. Évitez les erreurs administratives et maximisez votre
            réduction de prime.
          </p>
        </div>

        <div
          data-testid="simulator-container"
          className="mt-12 bg-white border-2 border-slate-100 rounded-2xl p-6 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <Progress value={progress} className="h-1 mb-8 bg-slate-200 [&>div]:bg-spi-red" />

          {step === 0 && (
            <div className="fade-up" data-testid="step-situation">
              <h3 className="font-display text-2xl text-spi-blue font-semibold">
                Quelle est votre situation familiale&nbsp;?
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                Cela nous permet d'identifier le bon barème applicable.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { v: "celibataire", t: "Célibataire", d: "Vous vivez seul·e" },
                  { v: "couple", t: "Couple", d: "Marié·e ou partenariat" },
                  { v: "famille", t: "Famille", d: "Couple avec enfants" },
                  { v: "famille_mono", t: "Famille monoparentale", d: "Un parent + enfants" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    data-testid={`situation-${opt.v}`}
                    onClick={() => handleSituation(opt.v)}
                    className={`text-left p-5 rounded-lg border transition-all ${
                      data.situation === opt.v
                        ? "border-spi-blue bg-spi-blue/5"
                        : "border-slate-200 hover:border-spi-blue/50"
                    }`}
                  >
                    <div className="font-semibold text-spi-blue">{opt.t}</div>
                    <div className="text-xs text-slate-500 mt-1">{opt.d}</div>
                  </button>
                ))}
              </div>

              {(data.situation === "famille" || data.situation === "famille_mono") && (
                <div className="mt-6 max-w-xs">
                  <Label htmlFor="enfants">Nombre d'enfants</Label>
                  <Input
                    id="enfants"
                    data-testid="input-enfants"
                    type="number"
                    min="0"
                    max="10"
                    value={data.enfants}
                    onChange={(e) => set("enfants", e.target.value)}
                    className="mt-2"
                  />
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="fade-up" data-testid="step-finances">
              <h3 className="font-display text-2xl text-spi-blue font-semibold">
                Vos revenus et votre fortune
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                Indiquez le revenu mensuel <strong>BRUT</strong> du foyer (avant déductions) et la fortune totale annuelle.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="revenu">Revenu mensuel BRUT (CHF) *</Label>
                  <Input
                    id="revenu"
                    data-testid="input-revenu"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder="ex. 5500"
                    value={data.revenu_mensuel_brut}
                    onChange={(e) => set("revenu_mensuel_brut", e.target.value)}
                    className="mt-2"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Salaire mensuel BRUT (avant impôts et cotisations sociales).
                  </p>
                </div>
                <div>
                  <Label htmlFor="fortune">Fortune totale (CHF)</Label>
                  <Input
                    id="fortune"
                    data-testid="input-fortune"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder="ex. 25000"
                    value={data.fortune}
                    onChange={(e) => set("fortune", e.target.value)}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="fade-up" data-testid="step-prime">
              <h3 className="font-display text-2xl text-spi-blue font-semibold">
                Votre canton et votre prime actuelle
              </h3>
              <p className="text-slate-600 mt-2 text-sm">
                Sélectionnez votre canton de domicile. La prime est optionnelle.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Canton de domicile *</Label>
                  <Select
                    value={data.canton}
                    onValueChange={(v) => set("canton", v)}
                  >
                    <SelectTrigger className="mt-2" data-testid="select-canton">
                      <SelectValue placeholder="Choisir un canton" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {CANTONS.map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`canton-${c.code}`}>
                          {c.name} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="prime">Prime LAMal mensuelle (CHF, optionnel)</Label>
                  <Input
                    id="prime"
                    data-testid="input-prime"
                    type="number"
                    min="0"
                    inputMode="numeric"
                    placeholder="ex. 540"
                    value={data.prime_mensuelle}
                    onChange={(e) => set("prime_mensuelle", e.target.value)}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div className="fade-up" data-testid="step-result">
              {result.eligible ? (
                <div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-7 h-7 text-emerald-700" strokeWidth={1.75} />
                    <h3 className="font-display text-2xl text-spi-blue font-semibold">
                      Vous êtes potentiellement éligible
                    </h3>
                  </div>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                        Subside mensuel estimé
                      </div>
                      <div className="font-display text-3xl text-spi-blue mt-2 font-bold">
                        {formatCHF(result.subside_total_mensuel)}
                      </div>
                    </div>
                    <div className="p-6 rounded-xl bg-spi-red/5 border border-spi-red/20">
                      <div className="text-xs uppercase tracking-wider text-spi-red font-bold">
                        Subside annuel estimé
                      </div>
                      <div className="font-display text-3xl text-spi-red mt-2 font-bold">
                        {formatCHF(result.subside_total_annuel)}
                      </div>
                    </div>
                    {result.reste_a_charge_mensuel !== null && result.reste_a_charge_mensuel !== undefined && (
                      <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Reste à charge mensuel
                        </div>
                        <div className="font-display text-3xl text-spi-blue mt-2 font-bold">
                          {formatCHF(result.reste_a_charge_mensuel)}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="mt-6 text-slate-700">{result.message}</p>
                  {result.methode && (
                    <p className="mt-2 text-xs text-slate-500">
                      Méthode de calcul appliquée&nbsp;:&nbsp;
                      <span className="font-semibold text-spi-blue">{result.methode}</span>
                      {result.canton ? ` · Canton ${result.canton}` : ""}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-7 h-7 text-amber-700" strokeWidth={1.75} />
                    <h3 className="font-display text-2xl text-spi-blue font-semibold">
                      A priori non éligible aux subsides
                    </h3>
                  </div>
                  <p className="mt-4 text-slate-700">{result.message}</p>
                  <p className="mt-2 text-slate-600 text-sm">
                    Nous pouvons toutefois auditer votre couverture actuelle pour
                    identifier des économies sur votre LAMal et vos compléments.
                  </p>
                </div>
              )}

              <div
                data-testid="simulator-disclaimer"
                className={`mt-8 p-4 rounded-lg text-xs leading-relaxed border ${
                  result.is_approximation
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-slate-50 border-slate-200 text-slate-700"
                }`}
              >
                {result.is_approximation && (
                  <div className="font-bold uppercase tracking-wider text-amber-800 text-[10px] mb-1">
                    Approximation — Vérification cantonale requise
                  </div>
                )}
                {result.disclaimer}
              </div>

              {/* === LEAD CAPTURE — recevez votre estimation par email === */}
              {!leadSent ? (
                <form
                  onSubmit={submitLead}
                  data-testid="simulator-lead-form"
                  className="mt-10 p-6 md:p-8 rounded-2xl bg-spi-blue text-white"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-spi-red flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div>
                      <h4 className="font-display text-xl font-bold leading-tight">
                        Recevez votre estimation détaillée par email
                      </h4>
                      <p className="text-sm text-slate-300 mt-1">
                        Et un appel gratuit d'un conseiller sous 24h ouvrées pour
                        finaliser votre dossier.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lead-prenom" className="text-slate-200">Prénom *</Label>
                      <Input
                        id="lead-prenom"
                        data-testid="lead-prenom"
                        required
                        value={lead.prenom}
                        onChange={(e) => setL("prenom", e.target.value)}
                        className="mt-2 bg-white/95 border-0 text-slate-900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lead-nom" className="text-slate-200">Nom *</Label>
                      <Input
                        id="lead-nom"
                        data-testid="lead-nom"
                        required
                        value={lead.nom}
                        onChange={(e) => setL("nom", e.target.value)}
                        className="mt-2 bg-white/95 border-0 text-slate-900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lead-email" className="text-slate-200">Email *</Label>
                      <Input
                        id="lead-email"
                        type="email"
                        data-testid="lead-email"
                        required
                        value={lead.email}
                        onChange={(e) => setL("email", e.target.value)}
                        className="mt-2 bg-white/95 border-0 text-slate-900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lead-tel" className="text-slate-200">Téléphone *</Label>
                      <Input
                        id="lead-tel"
                        data-testid="lead-telephone"
                        required
                        value={lead.telephone}
                        onChange={(e) => setL("telephone", e.target.value)}
                        className="mt-2 bg-white/95 border-0 text-slate-900"
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex items-start gap-3 text-xs text-slate-300">
                    <Checkbox
                      id="lead-consent"
                      data-testid="lead-consent"
                      checked={lead.consent}
                      onCheckedChange={(v) => setL("consent", !!v)}
                      className="mt-1 border-white/40 data-[state=checked]:bg-spi-red data-[state=checked]:border-spi-red"
                    />
                    <Label htmlFor="lead-consent" className="text-slate-300 leading-relaxed font-normal cursor-pointer">
                      J'accepte que Swiss Premium Insurance m'envoie cette estimation et
                      me recontacte. Mes données ne sont jamais cédées à des tiers
                      (<a href="/politique-confidentialite" className="underline">politique de confidentialité</a>).
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={leadLoading}
                    data-testid="lead-submit"
                    className="mt-6 w-full bg-spi-red hover:bg-spi-red/90 text-white h-12 font-semibold"
                  >
                    {leadLoading ? "Envoi en cours…" : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Recevoir mon estimation par email
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div
                  data-testid="simulator-lead-success"
                  className="mt-10 p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" strokeWidth={1.5} />
                  <h4 className="mt-3 font-display text-xl text-spi-blue font-bold">
                    Demande envoyée avec succès
                  </h4>
                  <p className="mt-3 text-slate-700 text-sm leading-relaxed max-w-md mx-auto">
                    Nous avons bien reçu votre demande de subside concernant votre
                    assurance maladie. Un conseiller vous recontactera sous 24h ouvrées.
                  </p>
                  <p className="mt-3 text-[10px] text-slate-500 leading-snug max-w-md mx-auto">
                    Conformément au RGPD/LPD, vos données sont utilisées uniquement
                    pour traiter votre demande et ne sont jamais cédées à des tiers.
                  </p>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  data-testid="result-cta-contact"
                  className="bg-spi-blue hover:bg-spi-blue-dark text-white"
                >
                  <a href="#contact">Être recontacté par un conseiller</a>
                </Button>
                <Button
                  variant="outline"
                  data-testid="result-reset"
                  onClick={reset}
                  className="border-slate-300"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Refaire une simulation
                </Button>
              </div>
            </div>
          )}

          {step < totalSteps && (
            <div className="mt-12 flex items-center justify-between">
              <Button
                variant="ghost"
                disabled={step === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="simulator-prev"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>

              {step < totalSteps - 1 ? (
                <Button
                  disabled={!validateStep()}
                  onClick={() => setStep((s) => s + 1)}
                  data-testid="simulator-next"
                  className="bg-spi-blue hover:bg-spi-blue-dark text-white"
                >
                  Suivant
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  disabled={loading || !validateStep()}
                  onClick={submit}
                  data-testid="simulator-submit"
                  className="bg-spi-red hover:bg-spi-red/90 text-white h-14 px-8 font-bold text-base"
                >
                  {loading ? "Calcul en cours…" : "Vérifier mon éligibilité"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          )}

          {step === totalSteps - 1 && !result && (
            <p className="mt-4 text-[11px] text-slate-500 text-right">
              Estimation gratuite basée sur les barèmes officiels.
              Vos données restent strictement confidentielles.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
